import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

/**
 * User-facing theme preferences. `system` defers to the OS color-scheme
 * media query; `light` / `dark` are explicit overrides that persist until
 * the user picks `system` again.
 */
export type ThemePreference = 'light' | 'dark' | 'system'

/** The theme actually applied to the DOM — no `system` here. */
type AppliedTheme = 'light' | 'dark'

const STORAGE_KEY = 'moria:theme'
const DARK_CLASS = 'moria-dark'

function readStoredPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored === 'light' || stored === 'dark') return stored
  } catch {
    /* localStorage unavailable — fall through to system default. */
  }
  return 'system'
}

function persistPreference(preference: ThemePreference): void {
  try {
    if (preference === 'system') {
      localStorage.removeItem(STORAGE_KEY)
    } else {
      localStorage.setItem(STORAGE_KEY, preference)
    }
  } catch {
    /* Swallow — failure to persist is not fatal; preference remains in memory. */
  }
}

export const useThemeStore = defineStore('theme', () => {
  const darkMediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
  const systemPrefersDark = ref(darkMediaQuery.matches)
  const preference = ref<ThemePreference>(readStoredPreference())

  const appliedTheme = computed<AppliedTheme>(() => {
    if (preference.value === 'system') {
      return systemPrefersDark.value ? 'dark' : 'light'
    }
    return preference.value
  })

  function applyToDOM(): void {
    const html = document.documentElement
    if (appliedTheme.value === 'dark') {
      html.classList.add(DARK_CLASS)
    } else {
      html.classList.remove(DARK_CLASS)
    }
  }

  function setPreference(next: ThemePreference): void {
    preference.value = next
    persistPreference(next)
    // Apply synchronously so callers observing the DOM see the effect on
    // the same tick. The watch below keeps system-triggered changes in
    // sync as a safety net.
    applyToDOM()
  }

  // Sync the DOM any time the resolved theme changes (initial + subsequent
  // system-preference-driven updates).
  watch(appliedTheme, applyToDOM, { immediate: true, flush: 'sync' })

  // Track OS preference changes so `system` mode stays live.
  darkMediaQuery.addEventListener('change', (e) => {
    systemPrefersDark.value = e.matches
  })

  return {
    preference,
    appliedTheme,
    setPreference,
  }
})
